"use client"

import React, { useEffect, useRef } from "react"

import { Button } from "@coval/ui"
import { RotateCcw, Volume2, VolumeOff } from "lucide-react"

import { ConversationViewer } from "@/components/transcript/ConversationViewer"

import { AGENT, PERSONA } from "../routes/simulationRoomTypes"
import type { LiveSimulationTranscript } from "../routes/useLiveSimulationTranscript"
import { formatElapsed } from "../utils/formatElapsed"
import { SpeakerMeter } from "./SpeakerMeter"

/**
 * One live simulation, rendered the same way everywhere: the running
 * simulation's transcript streams into the regular conversation viewer, with
 * a chrome bar (timer, speaker meters, Listen/Mute or Retry) above it while
 * the session can still produce audio. The session runs muted until the user
 * presses Listen — that gesture is what brings sound in.
 *
 * Pure presentation over a `useLiveSimulationTranscript` result: the caller
 * owns the hook (and keys this mount on the simulation id) so it can also
 * react to the session — e.g. the simulation detail page invalidates the
 * stored transcript when the room completes.
 */
export function LiveSimulationView({
  live,
  agentLabel,
  userLabel,
  agentAvatarUrl,
  userAvatarUrl,
  agentAvatarTint,
  userAvatarTint,
  isTextSimulation = false,
}: {
  live: LiveSimulationTranscript
  agentLabel?: string
  userLabel?: string
  agentAvatarUrl?: string
  userAvatarUrl?: string
  agentAvatarTint?: string
  userAvatarTint?: string
  /** Text-based simulations have no audio — the toggle stays put, disabled. */
  isTextSimulation?: boolean
}) {
  const {
    isLive,
    roomStatus,
    liveTranscript,
    isMuted,
    toggleMute,
    retryConnection,
    elapsedSeconds,
    levels,
  } = live

  // Keep the newest turn in view while the user stays at the live edge.
  // Scrolling up to reread releases the pin; scrolling back down re-pins.
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const pinnedToBottomRef = useRef(true)
  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (container === null) {
      return
    }
    pinnedToBottomRef.current =
      container.scrollHeight - container.scrollTop - container.clientHeight < 48
  }
  const latestMessage = liveTranscript[liveTranscript.length - 1]
  useEffect(() => {
    const container = scrollContainerRef.current
    if (container === null || !pinnedToBottomRef.current) {
      return
    }
    container.scrollTop = container.scrollHeight
  }, [latestMessage?.content, liveTranscript.length])

  const canListen =
    roomStatus === "waiting" ||
    roomStatus === "connected" ||
    roomStatus === "live"
  // A session that failed or stalled while the simulation is still running
  // can be restarted — auto-join only starts from idle, so without this the
  // transcript would stay dead for the rest of the call.
  const canRetry =
    isLive && (roomStatus === "error" || roomStatus === "stalled")

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      {canListen || canRetry ? (
        <div className="flex shrink-0 items-center gap-3 border-b border-surface-2 px-2 py-1">
          <span className="font-mono text-xs text-coval-text-muted px-2">
            {formatElapsed(elapsedSeconds)}
          </span>
          {isTextSimulation ? null : (
            // The meters only move while audio is unmuted — a muted
            // session's suspended audio graph reads silence.
            <div className="flex min-w-0 max-w-56 flex-1 flex-col gap-0.5">
              <SpeakerMeter label="Agent" level={levels[AGENT] ?? 0} />
              <SpeakerMeter label="Persona" level={levels[PERSONA] ?? 0} />
            </div>
          )}
          {canRetry ? (
            <Button
              className="ml-auto shrink-0"
              variant="ghost"
              aria-label="Retry the live transcript connection"
              onClick={retryConnection}
            >
              <RotateCcw className="h-4 w-4" />
              Retry
            </Button>
          ) : isTextSimulation ? (
            <span className="ml-auto shrink-0 inline-flex">
              <Button
                icon
                tooltip="Text-based simulations have no audio."
                variant="ghost"
                disabled
                aria-label="Audio unavailable for text-based simulations"
              >
                <VolumeOff className="h-4 w-4" />
              </Button>
            </span>
          ) : (
            <Button
              icon
              className="ml-auto shrink-0"
              variant="ghost"
              tooltip={isMuted ? "Listen to live audio" : "Mute live audio"}
              onClick={toggleMute}
            >
              {isMuted ? (
                <VolumeOff className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      ) : null}
      {liveTranscript.length === 0 ? (
        <div className="flex flex-1 min-h-0 items-center justify-center px-6">
          <p className="text-sm text-coval-text-muted text-center">
            {roomStatus === "stalled"
              ? "The live transcript is not available for this simulation."
              : roomStatus === "error"
                ? "The live transcript connection dropped."
                : roomStatus === "complete"
                  ? "The simulation has finished."
                  : !isLive
                    ? "Simulation in progress — the live transcript starts with the call."
                    : "Waiting for the conversation to start…"}
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <ConversationViewer
            transcript={liveTranscript}
            showCopyButton={false}
            className="h-full"
            agentLabel={agentLabel}
            userLabel={userLabel}
            agentAvatarUrl={agentAvatarUrl}
            userAvatarUrl={userAvatarUrl}
            agentAvatarTint={agentAvatarTint}
            userAvatarTint={userAvatarTint}
            // The viewer scrolls in its own inner region — the pin/follow
            // logic has to hang off that element, not a wrapper.
            scrollRef={scrollContainerRef}
            onScroll={handleScroll}
          />
        </div>
      )}
    </div>
  )
}
